import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

type ButtonVariant = 'primary' | 'ghost' | 'icon' | 'danger';
type ButtonSize = 'sm' | 'md';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children?: ReactNode;
}

const variantClass: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  ghost: 'btn-ghost',
  icon: 'btn-icon',
  danger: 'btn-danger',
};

const sizeClass: Record<Exclude<ButtonVariant, 'icon'>, Record<ButtonSize, string>> = {
  primary: {
    sm: 'px-3 py-1.5 text-xs rounded-md',
    md: 'px-4 py-2 text-sm rounded-lg',
  },
  ghost: {
    sm: 'px-3 py-1.5 text-xs rounded-md',
    md: 'px-4 py-2 text-sm rounded-lg',
  },
  danger: {
    sm: 'px-3 py-1.5 text-xs rounded-md',
    md: 'px-4 py-2 text-sm rounded-lg',
  },
};

const iconSizeClass: Record<ButtonSize, string> = {
  sm: 'h-7 w-7',
  md: 'h-[34px] w-[34px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className = '', children, ...props }, ref) => {
    const base = variantClass[variant];
    const sizing = variant === 'icon'
      ? iconSizeClass[size]
      : sizeClass[variant][size];

    return (
      <button
        ref={ref}
        type="button"
        className={`${base} ${sizing} font-medium disabled:opacity-50 ${className}`}
        {...props}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
